import React, { useState, FC } from 'react';
import { useTranslate, usePermissions } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import { useFeatures } from '~/hooks';
import { EditForm } from '~/components/ra';
import { useApi } from '~/hooks';
import { getConfiguration, Configuration } from '~/api/solutions';
import { Typography } from '@material-ui/core';
import { Tabs, Tab } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { Categories, categorizeFeatures, l } from '~/utils/solutions';
import { CodeForm } from '../FeaturesConfig/CodeForm';
import { OnboardingForm } from '../FeaturesConfig/OnboardingForm';
import { OnboardingIndividualForm } from '../FeaturesConfig/OnboardingIndividualForm';
import { ProfileForm } from '../FeaturesConfig/ProfileForm';
import { PayrollForm } from '../FeaturesConfig/PayrollForm';
import { BeneficiaryForm } from '../FeaturesConfig/BeneficiaryForm';
import { QuickQuoteForm } from '../FeaturesConfig/QuickQuoteForm';
import { IndividualRemittanceForm } from '../FeaturesConfig/IndividualRemittanceForm';
import { BusinessRemittancePayoutForm } from '../FeaturesConfig/BusinessRemittancePayoutForm';
import { PartnerRemittancePayoutForm } from '../FeaturesConfig/PartnerRemittancePayoutForm';
import { ServiceChargeForm } from '../FeaturesConfig/ServiceChargeForm';
import { IndividualInvitationForm } from '../FeaturesConfig/IndividualInvitationForm';
import { CashDepositForm } from '../FeaturesConfig/CashDepositForm';
import { EmployeeNetworkForm } from '../FeaturesConfig/EmployeeNetworkForm';
import { useForm } from 'react-final-form';
import { isEmpty } from 'lodash';
import { RewardsForm } from '../FeaturesConfig/RewardForm';
import Alert from '@material-ui/lab/Alert';
import { PartnerIncomingTransferForm } from '../FeaturesConfig/ParnerIncomingTransferForm';
import { PayByCashForm } from '../FeaturesConfig/PayByCashForm';
import { CashCollectionForm } from '../FeaturesConfig/CashCollectionForm';
import CardIssuingForm from '../FeaturesConfig/CardIssuingForm';
import { useStyles } from '~/hooks/useSolutionsStyles';
import { RefundForm } from '../FeaturesConfig/RefundForm';
import { ReversalPartnerForm } from '../FeaturesConfig/ReversalPartnerForm';
import { IndividualAuthForm } from '../FeaturesConfig/IndividualAuthForm';
import { Solution } from '~/types';
import { Loader } from '~/components';
import { InvitationTransferForm } from '../FeaturesConfig/InvitationTransferForm';
import { TenantPublicApiAuthForm } from '../FeaturesConfig/TenantPublicApiAuthForm';
import { ObjectDraftsForm } from '../FeaturesConfig/ObjectDraftsForm';
import { ApprovalCasesForm } from '../FeaturesConfig/ApprovalCasesForm';
import { ReversalSupportForm } from '../FeaturesConfig/ReversalSupportForm';
import { DebitOrderForm } from '../FeaturesConfig/DebitOrderForm';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  const classes = useStyles();
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <div className={classes.formWrapper}>
            <Typography>{children}</Typography>
          </div>
        </Box>
      )}
    </div>
  );
};

interface FeaturesConfigTabProps {
  record: Solution;
  basePath: string;
  resource: string;
}

const FeaturesConfigTab: FC<FeaturesConfigTabProps> = (props) => {
  const { record, basePath, resource } = props;

  const classes = useStyles();
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(`featuresConfig.${key}`));

  const [
    { data: configurationRecord, loading: configurationRecordLoading },
    refetch,
  ] = useApi<Configuration>(() => getConfiguration(record?.id!), []);

  if (configurationRecordLoading) {
    return <Loader />;
  }
  if (isEmpty(record?.features)) {
    return (
      <Grid container>
        <Grid item xs={3} xl={2}>
          {t('noFeatures')}
        </Grid>
      </Grid>
    );
  }
  return (
    <>
      <Alert className={classes.marginBottom} severity='warning'>
        {t('tabsAlert')}
      </Alert>
      <EditForm
        initialValues={configurationRecord}
        resource={resource}
        record={configurationRecord}
        resourceId={record.id}
        subresource='configuration'
        disabled={!permissions?.includes('solution.settings.update')}
        updateAfterSubmit={refetch}
        toolbarProps={{ style: { justifyContent: 'flex-end' } }}
      >
        <TabsLayout
          record={record}
          basePath={basePath}
          configurationRecord={configurationRecord}
          configurationRecordLoading={configurationRecordLoading}
        />
      </EditForm>
    </>
  );
};

interface TabsLayoutProps {
  record: Solution;
  basePath: string;
  configurationRecordLoading: boolean;
  configurationRecord?: Configuration;
}

const TabsLayout: FC<TabsLayoutProps> = ({
  record,
  basePath,
  configurationRecord,
  configurationRecordLoading,
}) => {
  const form = useForm();
  // Tabs state
  const [tabValue, setTabValue] = useState(1);
  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setTimeout(() => {
      setTabValue(newValue);
      form.reset();
    }, 100);
  };
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(`featuresConfig.${key}`));

  // Features, categories and config.
  const { list } = useFeatures();
  const features: string[] = (record?.features || []).sort((f1: string, f2: string) => {
    return list[f1]?.localeCompare(list[f2]);
  });
  const categorizedFeatures: Categories = categorizeFeatures(features);
  const categorizedArray = Object.entries(categorizedFeatures).reduce((acc, [category, items]) => {
    if (items.length > 0) {
      acc.push(category);
      acc.push(...items);
    }
    return acc;
  }, [] as string[]);
  // Tabs localization.
  const categoryLabels: { [x: string]: string } = {
    business: t('category.business'),
    individual: t('category.individual'),
    partner: t('category.partner'),
    other: t('category.other'),
  };
  return (
    <Grid container>
      <Grid item xs={3} xl={2}>
        <Tabs
          orientation='vertical'
          variant='scrollable'
          value={tabValue}
          onChange={handleTabChange}
          className={classes.tabs}
          scrollButtons='off'
        >
          {categorizedArray.map((feature, index) => (
            // Disabled tabs are used for categories.
            <Tab
              classes={{
                root: classes.tabRoot,
                disabled: classes.disabledTab,
                wrapper: classes.wrapper,
              }}
              disabled={
                feature === 'business' ||
                feature === 'individual' ||
                feature === 'partner' ||
                feature === 'other'
              }
              key={index}
              label={
                feature === 'business' ||
                feature === 'individual' ||
                feature === 'partner' ||
                feature === 'other'
                  ? categoryLabels[feature]
                  : list[feature]
              }
              id={`vertical-tab-${index}`}
              aria-controls={`vertical-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Grid>
      <Grid item xs={9} xl={10}>
        <TabPanel value={tabValue} index={tabValue}>
          {!configurationRecordLoading &&
            getFeatureItem(
              categorizedArray[tabValue],
              record,
              basePath,
              configurationRecordLoading,
              configurationRecord,
              t('noData')
            )}
        </TabPanel>
      </Grid>
    </Grid>
  );
};

const getFeatureItem = (
  key: string,
  record: any,
  basePath: any,
  configurationRecordLoading: boolean,
  configurationRecord: any,
  emptyText: string
) => {
  switch (key) {
    case 'veengu_individual_onboarding_self':
      return (
        <OnboardingIndividualForm
          record={record}
          basePath={basePath}
          keyName={key}
          configurationRecord={configurationRecord}
        />
      );
    case 'veengu_individual_onboarding_dashboard':
    case 'veengu_individual_onboarding_integration':
    case 'veengu_business_onboarding_self':
    case 'veengu_business_onboarding_dashboard':
    case 'veengu_business_onboarding_integration':
      return (
        <OnboardingForm
          record={record}
          basePath={basePath}
          keyName={key}
          configurationRecord={configurationRecord}
        />
      );
    case 'veengu_payroll':
      return <PayrollForm record={record} keyName={key} basePath={basePath} />;
    case 'veengu_business_profile':
      return <ProfileForm keyName={key} record={record} />;
    case 'veengu_individual_profile':
      return (
        <ProfileForm keyName={key} record={record} configurationRecord={configurationRecord} />
      );
    case 'veengu_individual_beneficiary_management':
    case 'veengu_business_beneficiary_management':
      return <BeneficiaryForm keyName={key} />;
    case 'veengu_quick_quote':
      return <QuickQuoteForm keyName={key} />;
    case 'veengu_individual_remittance':
      return <IndividualRemittanceForm keyName={key} basePath={basePath} />;
    case 'veengu_business_remittance_payout':
      return <BusinessRemittancePayoutForm keyName={key} basePath={basePath} />;
    case 'veengu_partner_remittance_payout':
      return <PartnerRemittancePayoutForm keyName={key} basePath={basePath} />;
    case 'veengu_service_charges':
      return <ServiceChargeForm keyName={key} />;
    case 'veengu_individual_invitation':
      return <IndividualInvitationForm keyName={key} />;
    case 'veengu_business_agentCashDeposit':
      return (
        <CashDepositForm
          loading={configurationRecordLoading}
          keyName={key}
          record={configurationRecord}
        />
      );
    case 'veengu_business_agentCashWithdrawal':
    case 'veengu_business_cashWithdrawal_byStaticQr':
    case 'veengu_business_purchase_byStaticQr':
    case 'veengu_business_purchase_byMobilePos':
    case 'veengu_business_ecommerce':
      return <CodeForm getSource={(field) => `${key}.${field}`} />;
    case 'veengu_business_employeeNetworkCreation':
      return (
        <EmployeeNetworkForm
          keyName={key}
          record={record}
          configurationRecord={configurationRecord}
          loading={configurationRecordLoading}
          basePath={basePath}
        />
      );
    case 'veengu_individual_invitation_rewards':
      return (
        <RewardsForm
          record={record}
          configurationRecord={configurationRecord}
          keyName={key}
          solutionId={record?.id}
        />
      );
    case 'veengu_partner_incoming_transfer_debit':
      return <PartnerIncomingTransferForm keyName={key} />;
    case 'veengu_partner_reversal':
      return <ReversalPartnerForm keyName={key} />;
    case 'veengu_business_payByCash':
      return <PayByCashForm keyName={key} />;
    case 'veengu_business_cashCollection':
      return <CashCollectionForm keyName={key} />;
    case 'veengu_individual_card_issuing':
      return <CardIssuingForm keyName={key} />;
    case 'veengu_refund':
      return <RefundForm keyName={key} basePath={basePath} record={record} />;
    case 'veengu_individual_authentication':
      return <IndividualAuthForm keyName={key} basePath={basePath} record={record} />;
    case 'veengu_invitation_transfer':
      return (
        <InvitationTransferForm
          keyName={key}
          basePath={basePath}
          record={record}
          configurationRecord={configurationRecord}
        />
      );
    case 'veengu_tenant_public_api_authentication':
      return <TenantPublicApiAuthForm keyName={key} />;
    case 'veengu_tenant_object_drafts':
      return <ObjectDraftsForm keyName={key} />;
    case 'veengu_tenant_approval_cases':
      return <ApprovalCasesForm keyName={key} />;
    case 'veengu_tenant_reversal_support':
      return <ReversalSupportForm keyName={key} />;
    case 'veengu_debit_order':
      return <DebitOrderForm keyName={key} />;
    default:
      return <div>{emptyText}</div>;
  }
};

export default FeaturesConfigTab;
